import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import Alert from '@material-ui/lab/Alert';
import Drawer from '@material-ui/core/Drawer';
import MuiLink from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import { Query, useQuery, useMutation } from 'react-apollo';
import Spin from 'components/common/Spin';
import IconButton from '@material-ui/core/IconButton';
import { GET_STUDENT_DATA } from 'graphql/engagement';
import { GET_STUDENT_PROFILE } from 'graphql/student/Profile';
import PersonIcon from '@material-ui/icons/Person';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import Button from 'components/common/Button';
import { toUserTimezone } from 'utils';
import TextMessages from 'components/pages/messages/sms/ConvoRoom';
import ChatMessages from 'modules/admin/student-chat-history';
import { UPDATE_ENGAGEMENT } from 'graphql/engagement';
import useSnack from 'hooks/useSnack';
import { Tab, Tabs } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: '100vw'
  },
  close: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  avatar: {
    width: 60,
    height: 60,
  },
  notes: {
    [theme.breakpoints.down('1200')]: {
      marginLeft: 0,
      marginTop: '10em',
    }
  },
  meetings: {
    [theme.breakpoints.down('1200')]: {
      marginLeft: 0,
      marginTop: 20,
    }

  },
  pendings:{
    [theme.breakpoints.down('950')]: {
      marginLeft: 0,
      marginTop: '18em',
    },
    position:'absolute',
    top:'15em',
    right:'16em',
  }
}));

const Engagement = ({ data }) => {
  const { timezone, type } = useSelector((s) => s.user.user);
  const [update] = useMutation(UPDATE_ENGAGEMENT);
  const [notes, setNotes] = useState(data.notes);
  const [score, setScore] = useState(data.score);
  const snack = useSnack();
  const { user, properties, timestamps } = data;
  const classes = useStyles();

  const onUpdateNote = async () => {
    await update({
      variables: {
        id: data.id,
        data: { notes },
      },
    });
    snack.success('Note updated');
  };

  const updateScore = async () => {
    await update({
      variables: {
        id: data.id,
        data: { score },
      },
    });
    snack.success('Score updated');
  };

  const updateStatus = async (property, value) => {
    await update({
      variables: {
        id: data.id,
        data: {
          properties: {
            ...data.properties,
            [property]: value,
          },
        },
      },
    });
    snack.success('Updated');
  };

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        <Grid item sm={12} md={8}>
          <Box marginTop={-2}>
            <TextField
              type="number"
              label="Score (0-10)"
              min={0}
              max={10}
              value={score}
              onChange={(e) => setScore(e.target.value)}
              size="small"
              variant="filled"
              InputProps={{
                disableUnderline: true,
              }}

            />
            <Button
              size="large"
              variant="contained"
              color="primary"
              onClick={updateScore}
            >
              Update
            </Button>
          </Box>
          <Box className={classes.meetings} marginLeft={50} marginTop={-20}>
            <Box mb={1}>
              <Typography>
                Last Login:{Array(30).fill('\xa0')}
                {toUserTimezone(user.last_login_at, timezone).format('L')}
              </Typography>
            </Box>

            <Box mb={1}>
              <Typography>
                Last Completed CC Meeting:{' '}
                {timestamps.last_completed_cc_meeting
                  ? toUserTimezone(
                    timestamps.last_completed_cc_meeting,
                    timezone,
                  ).format('L')
                  : 'N/A'}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography>
                Last Completed HC Meeting:{' '}
                {timestamps.last_completed_hc_meeting
                  ? toUserTimezone(
                    timestamps.last_completed_hc_meeting,
                    timezone,
                  ).format('L')
                  : 'N/A'}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography>
                Next Scheduled CC Meeting:{' '}
                {timestamps.next_cc_meeting
                  ? toUserTimezone(timestamps.next_cc_meeting, timezone).format('L')
                  : 'N/A'}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography>
                Next Scheduled HC Meeting:{' '}
                {timestamps.next_hc_meeting
                  ? toUserTimezone(timestamps.next_hc_meeting, timezone).format('L')
                  : 'N/A'}
              </Typography>
            </Box>
            <Typography>
              Last Message Received:{Array(8).fill('\xa0')}
              {timestamps.last_incoming_message
                ? toUserTimezone(
                  timestamps.last_incoming_message,
                  timezone,
                ).format('L')
                : 'N/A'}
            </Typography>
          </Box>
        </Grid>
        <Grid item sm={6} md={4}>
          <Box className={classes.pendings}>
            {properties.essayPendingReview ? (
              <Typography style={{ display: 'flex' }}>
                <WarningIcon
                  fontSize="small"
                  color="error"
                  onClick={() => updateStatus('essayPendingReview', false)}
                />{' '}
                Essay review pending.
              </Typography>
            ) : null}
            {properties.textAwatingResponse ? (
              <Typography style={{ display: 'flex' }}>
                <WarningIcon
                  fontSize="small"
                  color="error"
                  onClick={() => updateStatus('textAwatingResponse', false)}
                />{' '}
                Text response pending.
              </Typography>
            ) : null}
            {properties.chatAwatingResponse ? (
              <Typography style={{ display: 'flex' }}>
                <WarningIcon
                  fontSize="small"
                  color="error"
                  onClick={() => updateStatus('chatAwatingResponse', false)}
                />{' '}
                Chat response pending.
              </Typography>
            ) : null}
          </Box>
          <Box className={classes.notes} ml={5} mt={-17}>
            <Typography variant="body2">Notes:</Typography>
            <TextField
              fullWidth
              multiline
              variant="filled"
              InputProps={{
                disableUnderline: true,
              }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              label="Notes"
              margin="normal"
            />
            <Button
              onClick={onUpdateNote}
              size="small"
              variant="contained"
              color="primary"
            >
              Save
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};



const StudentDetails = ({ match, history }) => {
  const classes = useStyles();
  const [value, setValue] = useState(0);

  const onClose = () => {
    history.goBack();
  };

  return (
    <Drawer
      open
      anchor="right"
      onClose={onClose}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <Box>
        <Query query={GET_STUDENT_PROFILE} variables={{ id: match.params.id }}>
          {({ data, loading }) => {
            if (loading) return <Spin spinning linear />;

            const {
              user,
              guardians,
              admin,
              hc_user,
              lc_user,
              fc_user,
            } = data.student_profile[0];

            return (
              <Box mt={3} p={2}>
                <Grid container spacing={2}>
                  <Grid item md={1}>
                    <Avatar className={classes.avatar} src={user.user_pic}>
                      <PersonIcon />
                    </Avatar>
                  </Grid>
                  <Grid item sm={6} md={5}>
                    <Box mr={3}>
                      <Typography
                        gutterBottom
                        component={MuiLink}
                        href={`/students/${user.user_id}`}
                        target="_blank"
                        variant="subtitle1"
                      >
                        {user.fname} {user.lname}
                      </Typography>
                    </Box>

                    {guardians.map(({ guardian }, idx) => (
                      <Typography variant="body1">
                        Guardian {idx + 1}: {guardian.user_first_name}{' '}
                        {guardian.user_last_name} - {guardian.relationship}
                      </Typography>
                    ))}
                  </Grid>
                  <Grid item md={6}>
                    <Box>
                      <Typography variant="caption" component="div">
                        HC:{Array(10).fill('\xa0')}
                        {hc_user
                          ? `${hc_user.user_first_name} ${hc_user.user_last_name}`
                          : ''}
                      </Typography>
                    </Box>
                    <Box mt={1}>
                      <Typography variant="caption" component="div">
                        CC:{Array(10).fill('\xa0')}
                        {lc_user
                          ? `${lc_user.user_first_name} ${lc_user.user_last_name}`
                          : ''}
                      </Typography>
                    </Box>
                    <Box mt={1}>
                      <Typography variant="caption" component="div">
                        FA:{Array(11).fill('\xa0')}
                        {fc_user
                          ? `${fc_user.user_first_name} ${fc_user.user_last_name}`
                          : 'N/A'}
                      </Typography>
                    </Box>
                    <Box mt={1}>
                      <Typography variant="caption" component="div">
                        ADMIN:{Array(4).fill('\xa0')}
                        {admin
                          ? `${admin.user_first_name} ${admin.user_last_name}`
                          : ''}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            );
          }}
        </Query>
        <Query query={GET_STUDENT_DATA} variables={{ userId: match.params.id }}>
          {({ data, loading }) => {
            if (loading) return <Spin spinning linear />;

            const engagement = data.rows[0];

            if (!engagement)
              return <Alert severity="info">No Engagement Data found</Alert>;


            const handleTabs = (event, newValue) => {
              setValue(newValue);
            };


            return (
              <>
                <Engagement data={engagement} />
                <Box p={2} sx={{ width: '100%' }}>
                  <Tabs indicatorColor="primary"
                    textColor="primary"
                    value={value}
                    onChange={handleTabs}
                    aria-label="basic tabs example">

                    <Tab label="Text Message" />
                    <Tab label="Chat Message" />

                  </Tabs>
                  <TabPanel value={value} index={0}>
                    <TextMessages
                      history={history}
                      match={{
                        params: {
                          convoId: engagement.user.user_cognito_sub,
                        },
                      }}
                    /></TabPanel>
                  <TabPanel value={value} index={1}> <ChatMessages match={match} /></TabPanel>
                </Box>





              </>
            )

            function TabPanel(props) {
              const { children, value, index } = props
              return (
                <>
                  {
                    value === index && (
                      <h1>{children}</h1>
                    )
                  }
                </>
              )
            }
          }}
        </Query>
      </Box>
    </Drawer>
  );
};

export default StudentDetails;
